import React from 'react';
import PropTypes from 'prop-types';
import { Panel } from 'react-bootstrap';
import ClampLines from 'react-clamp-lines';
import humanizeString from 'humanize-string';
import classNames from 'classnames';
import uniqWith from 'lodash/uniqWith';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faThumbtack } from '@fortawesome/pro-solid-svg-icons';

import {  formatDateTime } from 'helpers/formatters/date_time_formats';
import TaskIcon from 'components/tasks/task_icon';
import './activity_card.scss';
import { htmlToString } from 'helpers/formatters/string_formats';
import InitialsBubble from 'components/initials_bubble';

export default class ActivityCard extends React.Component {
  static propTypes = {
    activityType: PropTypes.string.isRequired,
    associatedModels: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string.isRequired
    })),
    author: PropTypes.string.isRequired,
    authorImageUrl: PropTypes.string,
    authorInitials: PropTypes.string,
    body: PropTypes.string.isRequired,
    hasCrmEmail: PropTypes.bool,
    pinned: PropTypes.bool.isRequired,
    timestamp: PropTypes.string.isRequired,
    title: PropTypes.string,
    onClick: PropTypes.func
  }

  static defaultProps = {
    associatedModels: []
  }

  isClickable = () => !!this.props.onClick;

  handleClick = (e) => {
    const { onClick } = this.props;
    if (e.target.name !== 'contactLink' && onClick) onClick();
  }

  render() {
    const panelClass = classNames('activity-card', { clickable: this.isClickable() });

    return (
      <Panel className={panelClass} onClick={this.handleClick}>
        <Panel.Body>
          {this.renderHeader()}
          {this.renderBody()}
        </Panel.Body>
      </Panel>
    );
  }

  renderHeader() {
    const { activityType, authorImageUrl, authorInitials, author, pinned, timestamp } = this.props;
    const activity = activityType === 'other' ? 'activity' : activityType;

    return (
      <div className="header">
        {authorImageUrl ? (
          <img className="img-circle right-buffer-md author-photo" name="authorPhoto" src={authorImageUrl} />
        ) : (
          <InitialsBubble preview={false} text={authorInitials} />
        )}

        <div className="header-text">
          <div className="mb-1" name="logged-activity">
            <strong>{author}</strong>
            &nbsp;logged {`${/^[aeiou]/.test(activity) ? 'an' : 'a'} ${humanizeString(activity).toLowerCase()}`}
            {this.renderAssociatedModels()}
          </div>

          <div className="text-muted">
            <small>{ formatDateTime(timestamp)}</small>
            <span className="ml-1 d-inline-block align-middle">
              <TaskIcon size="xs" type={activityType} />
            </span>
          </div>
        </div>
        {pinned && (
          <p className="pin text-muted">
            <FontAwesomeIcon className="right-buffer-sm" icon={faThumbtack} size="sm" />
            <span className="hidden-xs">Pinned</span>
          </p>
        )}
      </div>
    );
  }

  renderAssociatedModels() {
    let { associatedModels } = this.props;
    if (associatedModels.length === 0) return null;

    associatedModels = uniqWith(associatedModels, (association1, association2) => {
      if (association1.associatedModelType !== association2.associatedModelType) return false;

      const assoc1Id = association1.originalId || association1.associatedModelId;
      const assoc2Id = association2.originalId || association2.associatedModelId;
      return assoc1Id === assoc2Id;
    });
    const modelCount = associatedModels.length;

    return (
      <span>
        {' with '}
        {associatedModels.map((model, i) => (
          <React.Fragment key={i}>
            {this.renderAssociatedModelLink(model)}
            {i < modelCount - 2 && ', '}
            {i === modelCount - 2 && ' and '}
          </React.Fragment>
        ))}
      </span>
    );
  }

  renderAssociatedModelLink(model) {
    if (!model.url) return model.name;

    return <a href={model.url} name="contactLink" rel="noopener noreferrer" target="_blank">{model.name}</a>;
  }

  renderBody() {
    const { title, body } = this.props;

    return (
      <div>
        <h5>{title}</h5>
        {this.renderContent(body)}
      </div>
    );
  }

  renderContent(body) {
    if (!body) return;

    let lines;

    if (this.props.hasCrmEmail) {
      body = htmlToString(body);
      lines = <ClampLines buttons={false} innerElement="p" lines={3} text={body}/>;
    } else {
      lines = body.split('\n').map((line, i) => <div key={i}>{line}</div>);
    }

    return <div className="activity-body">{lines}</div>;
  }
}
